import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { TOTAL_STEPS, encouragementMessages, formatCurrency, progressMap } from './onboardingConfig';
import { useFormSessionTracking, trackConverted, readDealerCode, readEntryPage, trafficSourceFields } from '../../lib/formSession';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

/**
 * GTM v1 onboarding: a 6-step anonymous lead-capture funnel.
 *
 * No accounts, no phone OTP, no documents. The applicant fills in who they are and their key
 * financial info, and the whole thing goes to the backend in ONE POST to /applications/lead,
 * which scores it, stores it and emits the events that put the lead in the Levr CRM.
 *
 * Because there's no separate "apply" step to drop out of, a lead reaches the CRM even if the
 * applicant closes the tab on the result screen.
 *
 * Phase 2 (phone verification, bank statements / Akahu, driver licence + selfie) is parked in
 * onboarding-steps/phase2/ and behind the backend's ENABLE_PHASE2_VERIFICATION flag.
 */
// Kept deliberately loose — these mirror the server's constraints only well enough to stop
// someone walking six steps before a 422 tells them their email was malformed. The server remains
// the authority; this is about not creating a dead end.
const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
// NZ mobile/landline, entered any way the user likes. canonical_phone() on the server is what
// actually validates; we only reject obviously-too-short input here.
const looksLikePhone = (v) => (v || '').replace(/[^\d]/g, '').length >= 8;
const isPositiveNumber = (v) => Number.parseFloat(v) > 0;


// Human-readable names for the funnel steps, so the CRM shows "employment" rather than "step 5".
const STEP_KEYS = {
  1: 'loan_type',
  2: 'calculator',
  3: 'personal_details',
  4: 'address',
  5: 'employment_income',
  6: 'debts_consent',
};

const getInitialFormData = () => ({
  loan_type: '',
  vehicle_category: '',
  borrow_amount: 30000,
  deposit_amount: 5000,
  phone: '',
  name: '',
  email: '',
  relationship_status: '',
  number_of_children: 0,
  address: '',
  address_duration_years: '',
  address_duration_months: '',
  living_situation: '',
  rent_mortgage_amount: '',
  rent_mortgage_frequency: 'weekly',
  employment_type: '',
  occupation: '',
  employer_name: '',
  employment_duration_years: '',
  employment_duration_months: '',
  income_amount: '',
  income_frequency: 'weekly',
  assets: {
    property: { selected: false, value: '' },
    vehicles: { selected: false, value: '' },
    boat: { selected: false, value: '' },
    other: { selected: false, value: '' },
  },
  liabilities: {
    home_loan: { selected: false, value: '' },
    vehicle_loan: { selected: false, value: '' },
    boat_loan: { selected: false, value: '' },
    personal_loan: { selected: false, value: '' },
    hire_purchase: { selected: false, value: '' },
    credit_cards: { selected: false, value: '' },
    other_debts: { selected: false, value: '' },
  },
  // Explicit "nothing to declare". The score's DTI component reads undeclared debts as zero, so
  // silence would quietly produce a more generous result than the applicant's real position —
  // unacceptable for a lending assessment. This turns skipping into a positive declaration.
  no_other_debts: false,
  consent: false,
  // From the URL, or handed over by whichever screen sent them here. A referral that survived the
  // hero and the quick-start round trip must not evaporate on the way into the long funnel.
  dealer_code: readDealerCode(),
  entry_page: readEntryPage(),
});

export const useOnboardingController = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [scoringInProgress, setScoringInProgress] = useState(false);
  const [scoringError, setScoringError] = useState(false);
  // The server's reason (rate limit, invalid phone, missing consent) rendered inline as well as
  // in the toast — a toast the user misses leaves them with no idea what to fix.
  const [scoringErrorMessage, setScoringErrorMessage] = useState('');
  const [showSpeakWithUs, setShowSpeakWithUs] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData);

  // Records once they actually start filling this in — not on page load — then on step changes,
  // after typing stops, and on page-hide. See useFormSessionTracking.
  useFormSessionTracking(React, {
    capture: 'full', step, stepKey: STEP_KEYS[step], totalSteps: TOTAL_STEPS, form: formData,
  });

  const totalSteps = TOTAL_STEPS;
  const progress = progressMap[step] || (step / totalSteps) * 100;
  const encouragement = encouragementMessages[step];
  const loanAmount = formData.borrow_amount - formData.deposit_amount;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAssetChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      assets: {
        ...prev.assets,
        [type]: { ...prev.assets[type], [field]: value },
      },
    }));
  };

  const handleLiabilityChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      liabilities: {
        ...prev.liabilities,
        [type]: { ...prev.liabilities[type], [field]: value },
      },
      // Selecting a debt clears "I have none" — the two are mutually exclusive, and resolving it
      // here means the user can never strand themselves in a contradictory state they can't undo.
      no_other_debts: field === 'selected' && value ? false : prev.no_other_debts,
    }));
  };

  /** Ticking "I have none" clears any debts already selected — the other half of the pair. */
  const handleNoOtherDebtsChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      no_other_debts: checked,
      liabilities: checked
        ? Object.fromEntries(
            Object.entries(prev.liabilities).map(([k, v]) => [k, { ...v, selected: false, value: '' }]),
          )
        : prev.liabilities,
    }));
  };

  /**
   * The single submission. Scores AND captures the lead in one call — the applicant is done
   * after this; everything they see afterwards is the result of it.
   */
  const handleSubmitApplication = async () => {
    // Guard against a double-click racing two leads through before the first response lands.
    // (The backend also dedupes within a 10-minute window, but don't rely on the round trip.)
    if (loading || scoringInProgress) return;

    setLoading(true);
    setScoringInProgress(true);
    setScoringError(false);
    setScoringErrorMessage('');
    try {
      const res = await fetch(`${API}/applications/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          consent: formData.consent,
          loan_type: formData.loan_type,
          vehicle_category: formData.vehicle_category,
          borrow_amount: formData.borrow_amount,
          deposit_amount: formData.deposit_amount,
          income_amount: parseFloat(formData.income_amount) || 0,
          income_frequency: formData.income_frequency,
          employment_type: formData.employment_type,
          employment_duration_years: parseInt(formData.employment_duration_years, 10) || 0,
          employment_duration_months: parseInt(formData.employment_duration_months, 10) || 0,
          occupation: formData.occupation,
          employer_name: formData.employer_name,
          address: formData.address,
          living_situation: formData.living_situation,
          address_duration_years: parseInt(formData.address_duration_years, 10) || 0,
          address_duration_months: parseInt(formData.address_duration_months, 10) || 0,
          rent_mortgage_amount: parseFloat(formData.rent_mortgage_amount) || 0,
          rent_mortgage_frequency: formData.rent_mortgage_frequency,
          relationship_status: formData.relationship_status,
          number_of_children: formData.number_of_children,
          assets: formData.assets,
          liabilities: formData.liabilities,
          no_other_debts: formData.no_other_debts,
          dealer_code: formData.dealer_code,
          entry_page: formData.entry_page,
          // Read at submit, not held in form state: it lives in the session like the entry page.
          ...trafficSourceFields(),
        }),
      });

      if (!res.ok) {
        // Surface the server's message — it carries the actionable cases (invalid phone,
        // missing consent, rate limit) that a generic error would hide.
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }

      const data = await res.json();
      setApprovalResult(data);
      setApplicationId(data.application_id || null);
      if (data.application_id) trackConverted(data.application_id);

      if (data.can_apply) {
        toast.success('Great news! You have a good chance of approval.');
      } else {
        toast.info("Thanks — we've got your details and some suggestions for you.");
      }
    } catch (error) {
      console.error('Submission error:', error);
      setScoringError(true);
      setScoringErrorMessage(error.message || '');
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      setScoringInProgress(false);
    }
  };

  const handleSpeakWithUs = async () => {
    setContactSubmitting(true);
    try {
      const res = await fetch(`${API}/contact/speak-with-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          loan_amount: loanAmount,
          likelihood_score: approvalResult?.score,
          advice_given: approvalResult?.advice,
          message: `Loan type: ${formData.loan_type}, Amount: ${formatCurrency(loanAmount)}`,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to submit');
      }

      setContactSubmitted(true);
      setShowSpeakWithUs(false);
      toast.success('Thank you! Our team will contact you soon.');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setContactSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.loan_type && (formData.loan_type === 'personal' || formData.vehicle_category);
      case 2:
        return formData.borrow_amount > formData.deposit_amount;
      case 3:
        return Boolean(
          formData.name.trim() &&
          looksLikeEmail(formData.email) &&
          looksLikePhone(formData.phone) &&
          formData.relationship_status,
        );
      case 4:
        return Boolean(formData.address.trim() && formData.living_situation);
      case 5:
        return Boolean(formData.employment_type && isPositiveNumber(formData.income_amount));
      case 6: {
        // Debts must be answered, not skipped — either the applicant declares they have none, or
        // every debt they tick carries an amount. A ticked-but-blank debt scores as $0, which
        // would flatter their affordability.
        const selectedDebts = Object.values(formData.liabilities).filter((l) => l.selected);
        const debtsAnswered = formData.no_other_debts
          ? selectedDebts.length === 0
          : selectedDebts.length > 0 && selectedDebts.every((l) => isPositiveNumber(l.value));
        return Boolean(formData.consent && debtsAnswered);
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (step === totalSteps) {
      handleSubmitApplication();
      return;
    }
    if (canProceed()) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  return {
    applicationId,
    approvalResult,
    canProceed,
    contactSubmitted,
    contactSubmitting,
    encouragement,
    formData,
    formatCurrency,
    handleAssetChange,
    handleBack,
    handleChange,
    handleLiabilityChange,
    handleNext,
    handleNoOtherDebtsChange,
    handleSpeakWithUs,
    handleSubmitApplication,
    loanAmount,
    loading,
    progress,
    returnHome: () => navigate('/'),
    scoringError,
    scoringErrorMessage,
    scoringInProgress,
    setShowSpeakWithUs,
    setStep,
    showSpeakWithUs,
    step,
    totalSteps,
  };
};

export default useOnboardingController;
